/* cart-drawer.js */

function isMobiLoudApp() {
  return document.documentElement.classList.contains("is-mobiloud-app");
}

function isIosMobiLoudApp() {
  const root = document.documentElement;
  return root.classList.contains("is-mobiloud-app") && root.classList.contains("is-ios-app");
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());

    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) overlay.addEventListener("click", this.close.bind(this));

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener("keydown", (event) => {
      if ((event.code || "").toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Clear the iOS back-gesture instant-close flag so this open animates normally.
    this.classList.remove("cart-drawer--back-immediate");
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden", "cart-drawer-open");
    if (window.NellaOverlayHistory && isMobiLoudApp()) window.NellaOverlayHistory.push(this);
  }

  close(options = {}) {
    const skipHistoryCleanup = !!options.skipHistoryCleanup;

    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden", "cart-drawer-open");

    if (window.NellaOverlayHistory) window.NellaOverlayHistory.pop(skipHistoryCleanup);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling?.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector(".drawer__inner");
    if (drawerInner?.classList.contains("is-empty")) {
      drawerInner.classList.remove("is-empty");
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const newHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );

      sectionElement.innerHTML = newHTML;

      // ✅ re-hydrate after replacing the drawer markup
      if (section.id === "cart-drawer") {
        const cdi = sectionElement.querySelector("cart-drawer-items");
        if (cdi) customElements.upgrade(cdi);
      }
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay")?.addEventListener("click", this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: "#CartDrawer" },
      { id: "cart-icon-bubble" },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  closeImmediate() {
    // Synchronous hard teardown — no slide/fade. The CSS transition is suppressed via
    // the iOS-scoped cart-drawer--back-immediate class (removed again on the next open),
    // so removing .active hides the drawer instantly. No history.back() — the popstate
    // handler already cleared the overlay slot, so release() is a no-op here.
    this.classList.add("cart-drawer--back-immediate");
    this.classList.remove("active", "animate");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden", "cart-drawer-open");
    if (window.NellaOverlayHistory) window.NellaOverlayHistory.release(this);
  }

  closeFromBackGesture() {
    // iOS Back/swipe: the native WKWebView back animation runs alongside the drawer's
    // own slide-out, producing a double animation. Hard-close instantly in iOS app mode;
    // every other platform (Android app, browser) keeps the animated close.
    if (isIosMobiLoudApp()) {
      this.closeImmediate();
      return;
    }
    this.close({ skipHistoryCleanup: true });
  }

  refreshFromCart() {
    // Re-sync the drawer + cart-icon sections from the live Shopify cart. Used after a
    // bfcache restore / app foreground, whose frozen snapshot can show a stale cart
    // (e.g. items added on another page) even though the Shopify cart itself is correct.
    // Read-only — never mutates the cart. A request token ignores a stale response if a
    // newer refresh / renderContents supersedes it. Reuses getSectionsToRender()/
    // getSectionInnerHTML() so the markup handling matches the add-to-cart render path.
    const self = this;
    const sections = this.getSectionsToRender();
    const ids = sections.map((s) => s.id).join(",");
    const token = (this._refreshToken = (this._refreshToken || 0) + 1);

    // The cart count was hidden synchronously on restore (cart-bubble-refreshing) to
    // avoid a visible stale -> live number flip. Reveal it once the live count is in, or
    // on failure / timeout, so it can never get stuck hidden.
    const clearRefreshing = function () {
      const iconEl = document.getElementById("cart-icon-bubble");
      if (iconEl) iconEl.classList.remove("cart-bubble-refreshing");
    };
    const safety = window.setTimeout(clearRefreshing, 3000);
    const done = function () {
      window.clearTimeout(safety);
      clearRefreshing();
    };

    fetch(`/?sections=${encodeURIComponent(ids)}`, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (token !== self._refreshToken) return; // superseded by a newer refresh/render
        if (data) {
          sections.forEach((section) => {
            const html = data[section.id];
            if (typeof html !== "string") return;
            const el = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!el) return;
            el.innerHTML = self.getSectionInnerHTML(html, section.selector);
            if (section.id === "cart-drawer") {
              const cdi = el.querySelector("cart-drawer-items");
              if (cdi) customElements.upgrade(cdi);
            }
          });
        }
        done();
      })
      .catch(() => {
        if (token !== self._refreshToken) return;
        done();
      });
  }
}

customElements.define("cart-drawer", CartDrawer);

// Before the browser freezes this page into bfcache, close any open cart
// drawer so the snapshot starts in the closed state. Prevents stale
// overflow-hidden / cart-drawer-open body classes surviving on restore.
// Safety net: pageshow catches any restore where pagehide did not clean up,
// or where the body class is stale but the drawer element lost its active class.
(function () {
  function getDrawer() {
    return document.querySelector("cart-drawer");
  }

  // Hide the cart count number BEFORE the restored frame paints, so the user never sees
  // the stale snapshot count flip to the live one. The class lives on the stable
  // #cart-icon-bubble element (survives its innerHTML replacement) and is removed by
  // refreshFromCart() once the live count is injected (or on failure/timeout).
  function markCartRefreshing() {
    var iconEl = document.getElementById("cart-icon-bubble");
    if (iconEl) iconEl.classList.add("cart-bubble-refreshing");
  }

  // App-mode cart re-sync. Throttled so it cannot fetch repeatedly (also dedupes if a
  // future fallback trigger is added). Read-only; never mutates the cart.
  var _lastAppCartRefresh = 0;
  function appCartRefresh() {
    if (!isMobiLoudApp()) return;
    var drawer = getDrawer();
    if (!drawer || typeof drawer.refreshFromCart !== "function") return;
    var now = window.performance && performance.now ? performance.now() : Date.now();
    if (now - _lastAppCartRefresh < 1500) return;
    _lastAppCartRefresh = now;
    markCartRefreshing();
    drawer.refreshFromCart();
  }

  window.addEventListener("pagehide", function (event) {
    if (!event.persisted) return;
    var drawer = getDrawer();
    if (drawer && drawer.classList.contains("active")) {
      drawer.close({ skipHistoryCleanup: true });
    }
  });

  window.addEventListener("pageshow", function (event) {
    if (!event.persisted) return;
    var drawer = getDrawer();
    if (!drawer) return;
    if (
      drawer.classList.contains("active") ||
      document.body.classList.contains("cart-drawer-open")
    ) {
      drawer.close({ skipHistoryCleanup: true });
    }
    // bfcache restored a frozen snapshot of the cart UI, which can be stale if the cart
    // changed on another page. The Shopify cart itself is correct; only the restored DOM
    // is stale. Re-sync the drawer + cart-icon from the live cart. App mode only.
    appCartRefresh();
  });

  // NOTE: a visibilitychange fallback (for home-tab pages MobiLoud may keep alive without
  // a persisted pageshow) was intentionally left out of this build. Test pageshow-persisted
  // first; only add the fallback if the home tab still misses the refresh on device.
})();

/* Wait until CartItems (from cart.js) is available before defining cart-drawer-items */
(function defineCartDrawerItemsWhenReady() {
  const Base = window.CartItems || customElements.get("cart-items");
  if (!Base) {
    setTimeout(defineCartDrawerItemsWhenReady, 0);
    return;
  }

  class CartDrawerItems extends Base {
    getSectionsToRender() {
      return [
        { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
        { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
})();
